
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { ActionTypes } from '@/store/actionTypes';
import FbxButton from '@/components/fbx-button.vue';
import FbxInput from '@/components/fbx-input.vue';
import InputModeToggle from '@/components/InputModeToggle.vue';
import CompanySelector from '@/components/CompanySelector.vue';
import CompanyLogo from './CompanyLogo.vue';
import { isNullOrUndefined } from '@/shared/helpers';
import FbaApiError from '@/types/FbaApiError';
import TrailerAerial from './TrailerAerial.vue';
import { SelectedQRSide } from '@/store/types';

export default defineComponent({
    name: 'ReportQrUnassociated',
    emits: ['onAttachTrailer', 'onReturnToScan', 'hideBackButton'],
    components: {
        CompanySelector,
        CompanyLogo,
        FbxInput,
        FbxButton,
        InputModeToggle,
        TrailerAerial,
    },
    data: (): {
        trailerAlert: string | null;
        trailerNumber: string | null;
        trailerCompany: string | null;
        isInputText: boolean;
        isLoading: boolean;
        messageIncorrectCompany: boolean;
        showTrailerSidePicker: boolean;
        selectedQRSide: SelectedQRSide | null;
    } => {
        return {
            trailerAlert: null,
            trailerCompany: null,
            trailerNumber: null,
            isInputText: false,
            isLoading: false,
            messageIncorrectCompany: false,
            showTrailerSidePicker: false,
            selectedQRSide: null,
        };
    },
    props: {
        scannedQrId: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
        }),
        submitDisabled(): boolean {
            return (
                !this.trailerNumber || !this.trailerCompany || this.isLoading
            );
        },
        driverCompany(): string {
            return this.driver?.company?.toLowerCase();
        },
        isAllowedCompany(): boolean {
            return (
                !isNullOrUndefined(this.trailerCompany?.toLowerCase()) &&
                this.trailerCompany?.toLowerCase() ===
                    this.driver?.company?.toLowerCase()
            );
        },
        actionText() {
            return this.isLoading ? 'Continuing' : 'Continue';
        },
    },
    methods: {
        ...mapActions({
            reportQrUnassociated: ActionTypes.REPORT_QR_UNASSOCIATED,
        }),
        onAttachAnotherTrailer() {
            this.$emit('onReturnToScan');
        },
        onFindTrailer() {
            this.$router.push({ name: 'DriverLanding' });
        },
        onSelectCompany(company: string) {
            this.trailerCompany = company;
        },
        onTrailerHasLetters(hasLetters: boolean) {
            this.isInputText = hasLetters;
        },
        showTrailerSidePickerOverlay() {
            this.showTrailerSidePicker = true;
        },
        hideTrailerSidePickerOverlay() {
            this.showTrailerSidePicker = false;
        },
        selectQRSide(side: SelectedQRSide) {
            this.selectedQRSide = side;
        },
        async onSubmit() {
            this.isLoading = true;

            const success = await this.reportQrUnassociated({
                trailerNumber: this.trailerNumber?.toUpperCase(),
                trailerCompany: this.trailerCompany?.toLowerCase(),
                scannedQrId: this.scannedQrId,
                selectedQRSide: this.selectedQRSide,
            });
            this.isLoading = false;

            if (success instanceof FbaApiError) {
                this.trailerAlert = 'Not found!';
                return;
            }

            if (this.isAllowedCompany) {
                this.$emit('onAttachTrailer', this.trailerNumber);
            } else {
                this.messageIncorrectCompany = true;
                this.hideTrailerSidePickerOverlay();
            }
        },
    },
    watch: {
        trailerNumber() {
            this.trailerAlert = null;
        },
    },
    mounted() {
        this.$emit('hideBackButton', true);
    },
    beforeUnmount() {
        this.$emit('hideBackButton', false);
    },
});
